import { useState, useMemo } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Cpu, Zap, Box, CircuitBoard, Info } from 'lucide-react';
import { gpuData, GPU } from '@/data/gpuData';
import { psus, cases, motherboards, gpuPhysicalSpecs, PSU, PCCase, Motherboard } from '@/data/pcComponents';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface CompatibilityResult {
  compatible: boolean;
  status: 'success' | 'warning' | 'error';
  message: string;
  details?: string;
}

interface BuildCompatibility {
  psu: CompatibilityResult | null;
  case: CompatibilityResult | null;
  motherboard: CompatibilityResult | null;
  overall: 'compatible' | 'warning' | 'incompatible' | 'incomplete';
}

const StatusIcon = ({ status }: { status: 'success' | 'warning' | 'error' }) => {
  if (status === 'success') return <CheckCircle2 className="w-5 h-5 text-green-500" />;
  if (status === 'warning') return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
  return <XCircle className="w-5 h-5 text-red-500" />;
};

export const BuildCompatibilityChecker = () => {
  const [selectedGPU, setSelectedGPU] = useState<GPU | null>(null);
  const [selectedPSU, setSelectedPSU] = useState<PSU | null>(null);
  const [selectedCase, setSelectedCase] = useState<PCCase | null>(null);
  const [selectedMotherboard, setSelectedMotherboard] = useState<Motherboard | null>(null);

  const gpuSpecs = selectedGPU ? gpuPhysicalSpecs[selectedGPU.id] : null;

  const compatibility = useMemo<BuildCompatibility>(() => {
    const result: BuildCompatibility = {
      psu: null,
      case: null,
      motherboard: null,
      overall: 'incomplete',
    };

    if (!selectedGPU || !gpuSpecs) return result;

    // PSU Compatibility
    if (selectedPSU) {
      const psuHeadroom = selectedPSU.wattage - gpuSpecs.recommendedPSU;
      if (selectedPSU.wattage >= gpuSpecs.recommendedPSU) {
        if (psuHeadroom >= 100) {
          result.psu = {
            compatible: true,
            status: 'success',
            message: 'PSU is compatible',
            details: `${selectedPSU.wattage}W provides ${psuHeadroom}W headroom above the ${gpuSpecs.recommendedPSU}W recommendation.`,
          };
        } else {
          result.psu = {
            compatible: true,
            status: 'warning',
            message: 'PSU is compatible but tight',
            details: `${selectedPSU.wattage}W meets the ${gpuSpecs.recommendedPSU}W recommendation with limited headroom (${psuHeadroom}W).`,
          };
        }
      } else {
        result.psu = {
          compatible: false,
          status: 'error',
          message: 'PSU is underpowered',
          details: `${selectedPSU.wattage}W is below the recommended ${gpuSpecs.recommendedPSU}W. You need at least ${gpuSpecs.recommendedPSU - selectedPSU.wattage}W more.`,
        };
      }
    }

    // Case Compatibility
    if (selectedCase) {
      const lengthOk = selectedCase.maxGPULength >= gpuSpecs.length;
      const slotsOk = selectedCase.maxGPUSlots >= gpuSpecs.slots;
      
      if (lengthOk && slotsOk) {
        const lengthClearance = selectedCase.maxGPULength - gpuSpecs.length;
        if (lengthClearance >= 30) {
          result.case = {
            compatible: true,
            status: 'success',
            message: 'Case is compatible',
            details: `GPU fits with ${lengthClearance}mm clearance. ${gpuSpecs.slots} slot GPU fits in ${selectedCase.maxGPUSlots} slot case.`,
          };
        } else {
          result.case = {
            compatible: true,
            status: 'warning',
            message: 'Case fits but tight',
            details: `GPU fits with only ${lengthClearance}mm clearance. Check if front fans or radiators might interfere.`,
          };
        }
      } else if (!lengthOk) {
        result.case = {
          compatible: false,
          status: 'error',
          message: 'GPU is too long',
          details: `GPU is ${gpuSpecs.length}mm but case only supports up to ${selectedCase.maxGPULength}mm. ${gpuSpecs.length - selectedCase.maxGPULength}mm too long.`,
        };
      } else {
        result.case = {
          compatible: false,
          status: 'error',
          message: 'Not enough expansion slots',
          details: `GPU requires ${gpuSpecs.slots} slots but case only supports ${selectedCase.maxGPUSlots} slot GPUs.`,
        };
      }
    }

    // Motherboard Compatibility
    if (selectedMotherboard) {
      if (selectedMotherboard.pcieSlots >= 1) {
        const isPcie5 = selectedMotherboard.pcieVersion === '5.0';
        const isNewerGPU = selectedGPU.releaseYear >= 2024;
        
        if (isPcie5 || !isNewerGPU) {
          result.motherboard = {
            compatible: true,
            status: 'success',
            message: 'Motherboard is compatible',
            details: `PCIe ${selectedMotherboard.pcieVersion} slot available. ${selectedMotherboard.formFactor} form factor.`,
          };
        } else {
          result.motherboard = {
            compatible: true,
            status: 'warning',
            message: 'Compatible but not optimal',
            details: `PCIe ${selectedMotherboard.pcieVersion} may slightly limit newer GPU performance. Consider PCIe 5.0 motherboard for best results.`,
          };
        }
      } else {
        result.motherboard = {
          compatible: false,
          status: 'error',
          message: 'No PCIe x16 slot',
          details: 'This motherboard has no PCIe x16 slot for the GPU.',
        };
      }
    }

    // Calculate overall compatibility
    const checks = [result.psu, result.case, result.motherboard].filter(Boolean);
    if (checks.length === 0) {
      result.overall = 'incomplete';
    } else if (checks.some(c => c?.status === 'error')) {
      result.overall = 'incompatible';
    } else if (checks.some(c => c?.status === 'warning')) {
      result.overall = 'warning';
    } else {
      result.overall = 'compatible';
    }

    return result;
  }, [selectedGPU, selectedPSU, selectedCase, selectedMotherboard, gpuSpecs]);

  const totalPrice = useMemo(() => {
    let price = selectedGPU?.msrp || 0;
    price += selectedPSU?.price || 0;
    price += selectedCase?.price || 0;
    price += selectedMotherboard?.price || 0;
    return price;
  }, [selectedGPU, selectedPSU, selectedCase, selectedMotherboard]);

  return (
    <section id="compatibility" className="py-16 bg-secondary/20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4">Build Helper</Badge>
          <h2 className="text-3xl font-bold mb-4">PC Build Compatibility Checker</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Select your components to check if they're compatible with your chosen GPU.
            We'll verify PSU wattage, case clearance, and motherboard compatibility.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Component Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* GPU Selection */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Cpu className="w-5 h-5 text-primary" />
                  Graphics Card
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedGPU?.id || ''}
                  onValueChange={(value) => setSelectedGPU(gpuData.find(g => g.id === value) || null)}
                >
                  <SelectTrigger aria-label="Select a graphics card">
                    <SelectValue placeholder="Select a GPU" />
                  </SelectTrigger>
                  <SelectContent>
                    {gpuData.map((gpu) => (
                      <SelectItem key={gpu.id} value={gpu.id}>
                        <span className="flex items-center gap-2">
                          <span className={cn(
                            'w-2 h-2 rounded-full',
                            gpu.brand === 'nvidia' && 'bg-nvidia',
                            gpu.brand === 'amd' && 'bg-amd',
                            gpu.brand === 'intel' && 'bg-intel'
                          )} />
                          {gpu.name} - ${gpu.msrp}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {gpuSpecs && (
                  <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="text-muted-foreground">Length</div>
                      <div className="font-semibold">{gpuSpecs.length}mm</div>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="text-muted-foreground">Slots</div>
                      <div className="font-semibold">{gpuSpecs.slots}</div>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="text-muted-foreground">Min PSU</div>
                      <div className="font-semibold">{gpuSpecs.recommendedPSU}W</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PSU Selection */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Power Supply
                  {compatibility.psu && <StatusIcon status={compatibility.psu.status} />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedPSU?.id || ''}
                  onValueChange={(value) => setSelectedPSU(psus.find(p => p.id === value) || null)}
                >
                  <SelectTrigger aria-label="Select a power supply">
                    <SelectValue placeholder="Select a PSU" />
                  </SelectTrigger>
                  <SelectContent>
                    {psus.map((psu) => (
                      <SelectItem key={psu.id} value={psu.id}>
                        {psu.name} - {psu.wattage}W {psu.efficiency} - ${psu.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {compatibility.psu && (
                  <div className={cn(
                    'mt-4 p-4 rounded-lg border',
                    compatibility.psu.status === 'success' && 'bg-green-500/10 border-green-500/30',
                    compatibility.psu.status === 'warning' && 'bg-yellow-500/10 border-yellow-500/30',
                    compatibility.psu.status === 'error' && 'bg-red-500/10 border-red-500/30'
                  )}>
                    <div className="flex items-center gap-2 font-semibold mb-1">
                      <StatusIcon status={compatibility.psu.status} />
                      {compatibility.psu.message}
                    </div>
                    <p className="text-sm text-muted-foreground">{compatibility.psu.details}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Case Selection */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Box className="w-5 h-5 text-blue-500" />
                  PC Case
                  {compatibility.case && <StatusIcon status={compatibility.case.status} />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedCase?.id || ''}
                  onValueChange={(value) => setSelectedCase(cases.find(c => c.id === value) || null)}
                >
                  <SelectTrigger aria-label="Select a PC case">
                    <SelectValue placeholder="Select a case" />
                  </SelectTrigger>
                  <SelectContent>
                    {cases.map((pcCase) => (
                      <SelectItem key={pcCase.id} value={pcCase.id}>
                        {pcCase.name} - {pcCase.formFactor} (max {pcCase.maxGPULength}mm) - ${pcCase.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {compatibility.case && (
                  <div className={cn(
                    'mt-4 p-4 rounded-lg border',
                    compatibility.case.status === 'success' && 'bg-green-500/10 border-green-500/30',
                    compatibility.case.status === 'warning' && 'bg-yellow-500/10 border-yellow-500/30',
                    compatibility.case.status === 'error' && 'bg-red-500/10 border-red-500/30'
                  )}>
                    <div className="flex items-center gap-2 font-semibold mb-1">
                      <StatusIcon status={compatibility.case.status} />
                      {compatibility.case.message}
                    </div>
                    <p className="text-sm text-muted-foreground">{compatibility.case.details}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Motherboard Selection */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CircuitBoard className="w-5 h-5 text-purple-500" />
                  Motherboard
                  {compatibility.motherboard && <StatusIcon status={compatibility.motherboard.status} />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedMotherboard?.id || ''}
                  onValueChange={(value) => setSelectedMotherboard(motherboards.find(m => m.id === value) || null)}
                >
                  <SelectTrigger aria-label="Select a motherboard">
                    <SelectValue placeholder="Select a motherboard" />
                  </SelectTrigger>
                  <SelectContent>
                    {motherboards.map((mobo) => (
                      <SelectItem key={mobo.id} value={mobo.id}>
                        {mobo.name} - {mobo.formFactor} ({mobo.socket}) - ${mobo.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {compatibility.motherboard && (
                  <div className={cn(
                    'mt-4 p-4 rounded-lg border',
                    compatibility.motherboard.status === 'success' && 'bg-green-500/10 border-green-500/30',
                    compatibility.motherboard.status === 'warning' && 'bg-yellow-500/10 border-yellow-500/30',
                    compatibility.motherboard.status === 'error' && 'bg-red-500/10 border-red-500/30'
                  )}>
                    <div className="flex items-center gap-2 font-semibold mb-1">
                      <StatusIcon status={compatibility.motherboard.status} />
                      {compatibility.motherboard.message}
                    </div>
                    <p className="text-sm text-muted-foreground">{compatibility.motherboard.details}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Panel */}
          <div className="space-y-6">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Build Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Overall Status */}
                <div className={cn(
                  'p-4 rounded-lg text-center',
                  compatibility.overall === 'compatible' && 'bg-green-500/10',
                  compatibility.overall === 'warning' && 'bg-yellow-500/10',
                  compatibility.overall === 'incompatible' && 'bg-red-500/10',
                  compatibility.overall === 'incomplete' && 'bg-muted'
                )}>
                  {compatibility.overall === 'compatible' && (
                    <>
                      <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <div className="font-semibold text-green-500">All Components Compatible!</div>
                    </>
                  )}
                  {compatibility.overall === 'warning' && (
                    <>
                      <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
                      <div className="font-semibold text-yellow-500">Compatible with Warnings</div>
                    </>
                  )}
                  {compatibility.overall === 'incompatible' && (
                    <>
                      <XCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                      <div className="font-semibold text-red-500">Compatibility Issues Found</div>
                    </>
                  )}
                  {compatibility.overall === 'incomplete' && (
                    <>
                      <Info className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                      <div className="font-semibold text-muted-foreground">Select Components</div>
                    </>
                  )}
                </div>

                {/* Selected Components */}
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">GPU</span>
                    <span className="font-medium">{selectedGPU?.name || '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">PSU</span>
                    <span className="font-medium">{selectedPSU?.name || '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Case</span>
                    <span className="font-medium">{selectedCase?.name || '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Motherboard</span>
                    <span className="font-medium">{selectedMotherboard?.name || '—'}</span>
                  </div>
                </div>

                {/* Total Price */}
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Estimated Total</span>
                    <span className="text-2xl font-bold">${totalPrice.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Prices are MSRP estimates and may vary
                  </p>
                </div>

                {/* Power Budget */}
                {selectedGPU && gpuSpecs && (
                  <div className="pt-4 border-t">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Power Budget</span>
                      <span className="font-medium">
                        {selectedGPU.tdp}W GPU / {gpuSpecs.recommendedPSU}W Recommended
                      </span>
                    </div>
                    <Progress 
                      value={selectedPSU ? (gpuSpecs.recommendedPSU / selectedPSU.wattage) * 100 : 0} 
                      className="h-2"
                    />
                    {selectedPSU && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {Math.round((gpuSpecs.recommendedPSU / selectedPSU.wattage) * 100)}% of PSU capacity recommended
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
